import { Add, Close, Edit } from '@mui/icons-material';
import BoltIcon from '@mui/icons-material/Bolt';
import { Box, Typography } from '@mui/material';
import { format as originalFormat } from 'date-fns';
import { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import {
    FlightTurnaroundDto,
    GetGetFlightsResponse,
    KeyGetGetFlights,
    ResourceAllocation,
    ResourceAllocationResponse,
    useMutationPutValidateResourcesAllocationFlights,
} from '../../../backend';
import { errorToastConfig, successToastConfig } from '../../../config/constants';
import { RoundButton } from '../../Button/RoundButton/RoundButton';
import { Popin, PopinCloseButton } from '../../navigation/Popin/Popin';
import { ganttDayFormat } from '../RmsContent/Gantt/gantt.constants';
import { useRmsGanttValue, useSetRmsGantt } from '../RmsContent/RmsContext';
import { StyledIcon } from './ResourcesAllocationResults.style';

export const ResourcesAllocationResults = memo(function ResourcesAllocationResults({
    results,
    onClose,
    turnarounds,
}: {
    results: ResourceAllocationResponse;
    onClose?: () => void;
    turnarounds: FlightTurnaroundDto[];
}) {
    const queryClient = useQueryClient();
    const day = useRmsGanttValue('day');
    const setProposedAllocation = useSetRmsGantt('proposedAllocation');
    const setNbUpdateAllocations = useSetRmsGantt('nbUpdateAllocations');
    const resourceType = useRmsGanttValue('resourceType');

    const allProposedAllocations = useMemo(() => {
        const array: ResourceAllocation[] = [];
        results.nonAllocatedOvercapacity?.map(o => array.push({ turnaround: o, resource: '' }));
        results.nonAllocatedNotAvailable?.map(o => array.push({ turnaround: o, resource: '' }));
        return results.proposedAllocation.concat(array);
    }, [results]);

    const { mutateAsync: validateProposal, isLoading } = useMutationPutValidateResourcesAllocationFlights({
        onSuccess: response => {
            queryClient.setQueriesData(
                {
                    queryKey: [
                        KeyGetGetFlights,
                        {
                            selectedDay: originalFormat(day, ganttDayFormat),
                            selectedResourceType: resourceType,
                        },
                    ],
                },
                (values: GetGetFlightsResponse): GetGetFlightsResponse => ({
                    ...values,
                    data: response.data,
                }),
            );
            toast(<FormattedMessage id={'schedule.allocate.successToast'} />, successToastConfig);
            setNbUpdateAllocations(0);
            setProposedAllocation(null);
            onClose?.();
        },
        onError: () => {
            toast(<FormattedMessage id={'schedule.allocate.errorToast'} />, errorToastConfig);
        },
    });

    const onClickValidateProposal = useCallback(
        () =>
            validateProposal({
                selectedDay: originalFormat(day, ganttDayFormat),

                selectedResourceType: resourceType,
                resourceAllocationValidationDto: {
                    turnarounds: turnarounds,
                    proposedAllocation: allProposedAllocations,
                },
            }),
        [validateProposal, day, resourceType, turnarounds, allProposedAllocations],
    );

    const onClickVerifyAllocation = useCallback(() => {
        setNbUpdateAllocations(results.report?.newAllocation + results.report?.updatedAllocation || 0);
        setProposedAllocation(allProposedAllocations);
        onClose?.();
    }, [
        onClose,
        allProposedAllocations,
        results.report?.newAllocation,
        results.report?.updatedAllocation,
        setNbUpdateAllocations,
        setProposedAllocation,
    ]);

    const hasChanges = results.report?.newAllocation || results.report?.updatedAllocation;

    return (
        <Popin
            width={700}
            sx={{
                '& .MuiDialogTitle-root': {
                    '& > .MuiSvgIcon-root:first-of-type': {
                        margin: '0',
                    },
                },
            }}
            title={
                <>
                    <BoltIcon sx={{ '& .:first-of-type': { margin: '0' } }} />
                    &ensp;
                    <Box flex={1}>
                        <FormattedMessage id="schedule.allocate.title" />
                    </Box>
                    <PopinCloseButton onClose={onClose} />
                </>
            }
            content={
                <>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        <StyledIcon sx={{ background: theme => theme.palette.success.main }}>
                            <Add />
                        </StyledIcon>
                        &ensp;
                        <Typography variant={'body1'} fontSize={'larger'}>
                            <FormattedMessage
                                id={'schedule.allocate.new'}
                                values={{ value: results.report?.newAllocation }}
                            />
                        </Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        <StyledIcon sx={{ background: theme => theme.palette.warning.main }}>
                            <Edit fontSize={'small'} />
                        </StyledIcon>
                        &ensp;
                        <Typography variant={'body1'} fontSize={'larger'}>
                            <FormattedMessage
                                id={'schedule.allocate.updated'}
                                values={{ value: results.report?.updatedAllocation }}
                            />
                        </Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} mb={2}>
                        <StyledIcon sx={{ background: theme => theme.palette.error.main }}>
                            <Close />
                        </StyledIcon>
                        &ensp;
                        <Typography variant={'body1'} fontSize={'larger'}>
                            <FormattedMessage
                                id={'schedule.allocate.nonAllocated'}
                                values={{ value: results.report?.nonAllocatedFlights }}
                            />
                        </Typography>
                    </Box>
                </>
            }
            actions={
                <>
                    <RoundButton color="primary" size="medium" variant="outlined" onClick={onClose}>
                        <FormattedMessage id="schedule.allocate.cancel" />
                    </RoundButton>
                    &emsp; &emsp; &emsp;
                    <RoundButton
                        color="primary"
                        size="medium"
                        variant="outlined"
                        onClick={onClickValidateProposal}
                        loading={isLoading}
                        disabled={isLoading || !hasChanges}
                    >
                        <FormattedMessage id="schedule.allocate.validate" />
                    </RoundButton>
                    &emsp;
                    <RoundButton
                        color="primary"
                        size="medium"
                        variant="contained"
                        onClick={onClickVerifyAllocation}
                        disabled={isLoading || !hasChanges}
                    >
                        <FormattedMessage id="schedule.allocate.verify" />
                    </RoundButton>
                </>
            }
        />
    );
});
